import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft, Calendar, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (!loading && err) {
    setTimeout(() => navigate("/blog"), 1200);
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <p className="text-sm text-zinc-500">Post not found — redirecting…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="blog-post-page">
      <Helmet>
        <title>{post ? `${post.title} | KoKo Sites Blog` : "Loading… | KoKo Sites Blog"}</title>
        {post?.excerpt && <meta name="description" content={post.excerpt} />}
        {post && <link rel="canonical" href={`https://kokosites.in/blog/${post.slug}`} />}
        {post && <meta property="og:title" content={post.title} />}
        {post?.excerpt && <meta property="og:description" content={post.excerpt} />}
        {post?.cover_image_url && <meta property="og:image" content={post.cover_image_url} />}
        <meta property="og:type" content="article" />
      </Helmet>
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-red-500 transition-colors mb-8"
        >
          <ArrowLeft size={12} /> All posts
        </Link>

        {loading || !post ? (
          <div className="space-y-4">
            <div className="h-10 bg-[#111] skeleton" />
            <div className="h-4 bg-[#111] skeleton w-1/3" />
            <div className="aspect-[16/9] bg-[#111] skeleton mt-8" />
            <div className="h-4 bg-[#111] skeleton mt-8" />
            <div className="h-4 bg-[#111] skeleton" />
            <div className="h-4 bg-[#111] skeleton w-3/4" />
          </div>
        ) : (
          <article>
            <header className="mb-8">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight mb-4 leading-[1.05]">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-widest text-zinc-500">
                <span className="flex items-center gap-1.5">
                  <User size={10} /> {post.author_name}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={10} /> {fmt(post.published_at || post.created_at)}
                </span>
              </div>
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {post.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 text-red-500 border border-red-600/30"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {post.cover_image_url && (
              <div className="aspect-[16/9] overflow-hidden mb-10 border border-white/10 bg-[#111]">
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div
              data-testid="blog-post-body"
              className="text-sm sm:text-base text-zinc-300 leading-relaxed whitespace-pre-wrap"
            >
              {post.body}
            </div>

            <div className="mt-14 pt-8 border-t border-white/10">
              <p className="text-xs text-zinc-500 mb-4">Got a project in mind?</p>
              <a
                href="https://wa.me/919242478118?text=Hi%20Kamran!%20I%20read%20a%20post%20on%20your%20blog%20and%20wanted%20to%20chat."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brutal btn-shine inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-8 text-xs border-2 border-white hover:bg-white hover:text-black"
              >
                Start a conversation
              </a>
            </div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
